import * as THREE from 'three'
import type { AssemblyTransform } from './assembly-document-types'

/**
 * 创建可写入 `_assembly.json` 的装配变换矩阵副本。
 *
 * 设计说明：装配文档是项目模型重载、版本切换与组件插入的权威结构来源，
 * 所有入口都必须在 cad-core 内统一校验矩阵长度与数值合法性，避免 UI 层散落 tuple 断言。
 *
 * @param matrix 待收口的 4x4 row-major 展平矩阵。
 * @param context 调用方上下文，用于错误日志定位。
 * @returns 已复制且满足 `AssemblyTransform` 契约的矩阵。
 * @throws 当矩阵不是 16 个元素或包含非有限数值时。
 */
export function createAssemblyTransform(matrix: readonly number[], context: string): AssemblyTransform {
  if (matrix.length !== 16) {
    throw new Error(`assembly transform invalid: ${context}, length=${matrix.length}`)
  }

  const invalidIndex = matrix.findIndex((value) => !Number.isFinite(value))

  if (invalidIndex >= 0) {
    throw new Error(
      `assembly transform invalid: ${context}, index=${invalidIndex}, value=${String(matrix[invalidIndex])}`,
    )
  }

  return [
    matrix[0], matrix[1], matrix[2], matrix[3],
    matrix[4], matrix[5], matrix[6], matrix[7],
    matrix[8], matrix[9], matrix[10], matrix[11],
    matrix[12], matrix[13], matrix[14], matrix[15],
  ]
}

/**
 * 将 `_assembly.json` 的 row-major 变换数组转换为 Three.js 矩阵。
 *
 * @param transform `_assembly.json` 中的 4x4 row-major 变换数组，平移量位于 index 3/7/11。
 * @returns 可供 Three.js 计算使用的 `Matrix4` 实例。
 * @throws 当变换数组不是 16 个元素时。
 */
export function matrixFromAssemblyTransform(transform: readonly number[]): THREE.Matrix4 {
  if (transform.length !== 16) {
    throw new Error(`assembly transform invalid: expected 16 numbers, received ${transform.length}`)
  }

  const matrix = new THREE.Matrix4()

  // `Matrix4.set` 接收 row-major 参数，再由 Three.js 写入 column-major elements。
  matrix.set(
    transform[0], transform[1], transform[2], transform[3],
    transform[4], transform[5], transform[6], transform[7],
    transform[8], transform[9], transform[10], transform[11],
    transform[12], transform[13], transform[14], transform[15],
  )

  return matrix
}

/**
 * 将 Three.js 矩阵转换回 `_assembly.json` 的 row-major 变换数组。
 *
 * @param matrix Three.js 矩阵，内部 `elements` 为 column-major 顺序。
 * @returns `_assembly.json` 使用的 row-major 变换数组。
 */
export function assemblyTransformFromMatrix(matrix: THREE.Matrix4): AssemblyTransform {
  const elements = matrix.elements

  // 显式转置索引，避免把 column-major elements 误写为装配文档的 row-major 顺序。
  return [
    elements[0], elements[4], elements[8], elements[12],
    elements[1], elements[5], elements[9], elements[13],
    elements[2], elements[6], elements[10], elements[14],
    elements[3], elements[7], elements[11], elements[15],
  ]
}

/**
 * 将 Three.js 列主序矩阵元组转换为项目模型使用的行主序装配矩阵。
 *
 * 设计说明：`.puppy/assembly.json` 直接保存 `Matrix4.elements`，平移量位于 12/13/14；
 * `ModelAssetNode.localTransform` 与 `_assembly.json` 使用后端行主序契约，平移量位于 3/7/11。
 * 两种协议不能直接复用同一个数组，否则每次重载都会把旋转矩阵转置并导致朝向来回翻转。
 *
 * @param elements Three.js `Matrix4.elements` 顺序的 16 元素矩阵。
 * @returns 可写入项目模型与 `_assembly.json` 的行主序矩阵。
 * @throws 当矩阵不是 16 个元素或包含非有限数值时。
 */
export function assemblyTransformFromColumnMajorElements(
  elements: readonly number[],
): AssemblyTransform {
  if (elements.length !== 16) {
    throw new Error(`assembly transform invalid: expected 16 numbers, received ${elements.length}`)
  }

  return createAssemblyTransform([
    elements[0], elements[4], elements[8], elements[12],
    elements[1], elements[5], elements[9], elements[13],
    elements[2], elements[6], elements[10], elements[14],
    elements[3], elements[7], elements[11], elements[15],
  ], 'column-major puppy assembly transform')
}

/**
 * 按装配层级顺序相乘两个 row-major 装配变换。
 *
 * @param left 左侧父级或当前世界变换。
 * @param right 右侧子级局部变换。
 * @returns 相乘后的 row-major `AssemblyTransform`，便于继续写入装配文档。
 */
export function multiplyAssemblyTransforms(left: readonly number[], right: readonly number[]): AssemblyTransform {
  return assemblyTransformFromMatrix(
    matrixFromAssemblyTransform(left).multiply(matrixFromAssemblyTransform(right)),
  )
}

/**
 * 将已有 Three.js 左矩阵与 row-major 装配变换相乘。
 *
 * @param left 已经位于 Three.js 边界内的左侧矩阵。
 * @param right `_assembly.json` row-major 右侧变换。
 * @returns 新的 Three.js 矩阵；不会修改传入的 `left`。
 */
export function multiplyAssemblyMatrixByTransform(left: THREE.Matrix4, right: readonly number[]): THREE.Matrix4 {
  // clone 后再 multiply，避免调用方复用的世界矩阵被工具函数意外改写。
  return left.clone().multiply(matrixFromAssemblyTransform(right))
}

/**
 * 对 row-major 装配变换求逆。
 *
 * @param transform `_assembly.json` row-major 变换数组。
 * @returns 求逆后的 Three.js 矩阵，供后续矩阵链路继续计算。
 */
export function invertAssemblyTransform(transform: readonly number[]): THREE.Matrix4 {
  return matrixFromAssemblyTransform(transform).invert()
}
